import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import formatters from '../shared/formatters';

// A month is not 4 weeks and a year is not 52. Using the true averages keeps the
// monthly projection from understating by ~8% and the yearly from drifting.
const WEEKS_PER_MONTH = 4.345;
const WEEKS_PER_YEAR = 52.18;

// weightedHours only ever inflates scheduledHours (statutory-holiday shifts count
// at 1.5x), so any excess beyond this float-noise epsilon means the current week
// carries a holiday and the run-rate overstates.
const HOLIDAY_EPSILON = 0.01;

// How far this week's revenue must sit from the 13-week forecast average before we
// flag the run-rate as likely to over- or understate a typical period.
const ANOMALY_THRESHOLD = 0.1;

// Only the dollar and hour fields scale. Gross margin is a ratio, so it carries across
// unchanged rather than multiplying; client/caregiver counts are distinct headcounts
// that cannot be extrapolated and are intentionally excluded.
const PROJECTION_ROWS = [
  {key: 'revenue', labelExpression: 'metrics.ClientMetrics.Projections.Revenue', kind: 'currency'},
  {key: 'laborCost', labelExpression: 'metrics.ClientMetrics.Projections.Labor', kind: 'currency'},
  {key: 'grossProfit', labelExpression: 'metrics.ClientMetrics.Projections.GrossProfit', kind: 'currency'},
  {key: 'scheduledHours', labelExpression: 'metrics.ClientMetrics.Projections.ScheduledHours', kind: 'hours'},
  {key: 'grossMargin', labelExpression: 'metrics.ClientMetrics.Projections.GrossMargin', kind: 'margin', carry: true},
];

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  color: '#A8A49E',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #EAEAEA',
};

const bodyCellSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F2F0EC',
};

const labelCellSx = {
  ...bodyCellSx,
  fontWeight: 600,
  color: '#6B6761',
};

// Multipliers read cleanly without locale formatting; keep the exact constants.
const formatMultiplier = (value) => `${value}`;

const meanOfField = (weeks, weekKeys, field) => {
  const values = weekKeys
      .map((weekKey) => weeks?.[weekKey]?.[field])
      .filter((value) => !formatters.isMissingValue(value));

  if (!values.length) {
    return null;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

// Which distortion, if any, to surface. Holiday weighting is definitive and takes
// priority; otherwise a large deviation from the forecast average signals that a
// single week is unrepresentative of a typical month or year.
const resolveCaveat = (thisWeek, forecastMeanRevenue) => {
  if (!thisWeek) {
    return null;
  }

  const {scheduledHours, weightedHours, revenue} = thisWeek;

  const hasHolidayWeighting =
    !formatters.isMissingValue(scheduledHours) &&
    !formatters.isMissingValue(weightedHours) &&
    weightedHours > scheduledHours + HOLIDAY_EPSILON;

  if (hasHolidayWeighting) {
    return {severity: 'warning', expression: 'metrics.ClientMetrics.Projections.HolidayWarning'};
  }

  if (
    !formatters.isMissingValue(revenue) &&
    !formatters.isMissingValue(forecastMeanRevenue) &&
    forecastMeanRevenue > 0
  ) {
    const deviation = (revenue - forecastMeanRevenue) / forecastMeanRevenue;

    if (deviation < -ANOMALY_THRESHOLD) {
      return {severity: 'info', expression: 'metrics.ClientMetrics.Projections.BelowAverageWarning'};
    }

    if (deviation > ANOMALY_THRESHOLD) {
      return {severity: 'info', expression: 'metrics.ClientMetrics.Projections.AboveAverageWarning'};
    }
  }

  return null;
};

const ProjectionsSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const {thisWeek, weekLabel, rows, caveat} = React.useMemo(() => {
    const weeks = currentSnapshot?.forecast?.weeks || {};
    const weekKeys = formatters.sortWeekKeysAscending(weeks);
    // The current ISO week is the first forecast bucket of the snapshot.
    const currentWeekKey = weekKeys.length ? weekKeys[0] : null;
    const currentWeek = currentWeekKey ? weeks[currentWeekKey] : null;
    const forecastMeanRevenue = meanOfField(weeks, weekKeys, 'revenue');

    const buildCells = (row) => {
      const base = currentWeek?.[row.key];

      if (formatters.isMissingValue(base)) {
        const dash = formatters.formatByKind(base, row.kind);

        return {week: dash, month: dash, year: dash};
      }

      // Ratios carry across every period unchanged instead of scaling.
      if (row.carry) {
        const carried = formatters.formatByKind(base, row.kind);

        return {week: carried, month: carried, year: carried};
      }

      return {
        week: formatters.formatByKind(base, row.kind),
        month: formatters.formatByKind(base * WEEKS_PER_MONTH, row.kind),
        year: formatters.formatByKind(base * WEEKS_PER_YEAR, row.kind),
      };
    };

    return {
      thisWeek: currentWeek,
      weekLabel: currentWeekKey ? formatters.formatWeekOfDate(currentWeekKey) : null,
      rows: PROJECTION_ROWS.map((row) => ({
        key: row.key,
        label: translate(row.labelExpression),
        cells: buildCells(row),
      })),
      caveat: resolveCaveat(currentWeek, forecastMeanRevenue),
    };
  }, [currentSnapshot]);

  const columns = [
    {key: 'metric', align: 'left', label: translate('metrics.ClientMetrics.Projections.Metric')},
    {key: 'week', align: 'right', label: translate('metrics.ClientMetrics.Projections.ThisWeek')},
    {key: 'month', align: 'right', label: translate('metrics.ClientMetrics.Projections.Monthly', {multiplier: formatMultiplier(WEEKS_PER_MONTH)})},
    {key: 'year', align: 'right', label: translate('metrics.ClientMetrics.Projections.Yearly', {multiplier: formatMultiplier(WEEKS_PER_YEAR)})},
  ];

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.Projections.SectionTitle' />
      {
        isLoading &&
        <PanelSkeleton height={220} />
      }
      {
        !isLoading &&
        !thisWeek &&
        <Alert severity='info'>
          <Translate expression='metrics.ClientMetrics.Projections.Empty' />
        </Alert>
      }
      {
        !isLoading &&
        thisWeek &&
        <Paper
          sx={{
            p: 3,
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            boxShadow: 'none',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '21px',
              color: '#1A1916',
              mb: 0.25,
            }}
          >
            <Translate expression='metrics.ClientMetrics.Projections.PanelTitle' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: '#A8A49E',
              mb: 2,
            }}
          >
            <Translate
              expression='metrics.ClientMetrics.Projections.PanelSubtitle'
              substitute={{week: weekLabel}}
            />
          </Typography>

          <Stack spacing={2}>
            {
              caveat &&
              <Alert severity={caveat.severity}>
                <Translate expression={caveat.expression} />
              </Alert>
            }

            <TableContainer sx={{overflowX: 'auto'}}>
              <Table size='small' sx={{minWidth: 520}}>
                <TableHead>
                  <TableRow>
                    {
                      columns.map((column) => (
                        <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                          {column.label}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell align='left' sx={labelCellSx}>
                          {row.label}
                        </TableCell>
                        <TableCell align='right' sx={bodyCellSx}>
                          {row.cells.week}
                        </TableCell>
                        <TableCell align='right' sx={bodyCellSx}>
                          {row.cells.month}
                        </TableCell>
                        <TableCell align='right' sx={bodyCellSx}>
                          {row.cells.year}
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      }
    </Box>
  );
};

ProjectionsSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(ProjectionsSection);
